import React, { useEffect, useState } from 'react';
import { Box, Button, Tooltip, Stack, Paper, Drawer, IconButton, Typography, FormControlLabel, Checkbox, TextField } from '@mui/material';
import { ImagePaths } from '../../../Utility/ImagePaths';
import { AmountsCard } from '../../../Components/CommonCards/CommonCards';
import { t } from 'i18next';
import { DataGrid } from '@mui/x-data-grid';
import { CommonGridToolBarWithFilterText, CommonPatientDetails } from '../../../Components/Common Components/CommonComponents';
import { AmountFormat, formatDate } from '../../../Components/CommonFunctions/CommonFunctions';
import RestAPIService from '../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../Utility/API/Serviceurls';
import CommonDatePicker from '../../../Components/Common Components/CommonDatePicker';
import { DateTime } from 'luxon';
import ToastMsg from '../../../Components/ToastMsg/ToastMsg';
import CommonValidation from '../../../Components/CommonFunctions/CommonValidation';
import { localGetItem } from '../../../Utility/Services/CacheProviderService';

const CurrencySymbol = "₹";  // Adjust the currency symbol as needed

const MakerApprovalTransaction = () => {
    const [dataList, setdataList] = useState([]);
    const [cardData, setCardData] = useState({})

    // pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // loading state
    const [isLoader, setIsLoader] = useState(false);

    // Drawer state management
    const [filterOpen, setFilterOpen] = useState(false);

    // Filter state management
    const [filterDatas, setFilterDatas] = useState({
        FromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        ToDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
        DateIndex: 1,
        patient_Name: "",
        UHID_No: "",
        phoneNumber: "",
        module_type: ['IP', 'OP', 'RADIOLOGY', 'LAB', ...localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : []],      // All selected
        status: ['Pending', 'Billed', 'Accepted'],       // All selected
        Patient_type: ['ip', 'op', 'self']
    });

    // error state
    const [isErrorMsg, setIsErrorMsg] = useState(false);
    const [isErrorMsgText, setIsErrorMsgText] = useState('');

    // error function
    const errorMessage = (message) => {
        setIsErrorMsg(true);
        setIsErrorMsgText(message);
    };

    // Checkbox handler
    const handleCheckboxChange = (field, value) => {
        setFilterDatas((prev) => {
            const currentValues = prev[field];
            const updatedValues = currentValues.includes(value)
                ? currentValues.filter((v) => v !== value)  // Remove if already selected
                : [...currentValues, value];                // Add if not selected
            return { ...prev, [field]: updatedValues };
        });
    };

    const handleDateChange = (fromDate, toDate, dateIndex) => {
        const today = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd');
        setFilterDatas((prev) => ({
            ...prev,
            FromDate: dateIndex === 1 ? today : fromDate,
            ToDate: dateIndex === 1 ? today : toDate,
            DateIndex: dateIndex
        }));
    };

    // Drawer handlers
    const handleOpenFilter = () => setFilterOpen(true);
    const handleCloseFilter = () => setFilterOpen(false);

    // API Call Function
    const fetchStatusData = async (params, isPrint = false) => {
        setIsLoader(true);
        let clinicId = localGetItem("clinicId") || localGetItem("SelectedDoctorClinicId");
        try {
            const url = `${Serviceurls.NEW_BILLING_PENDING_LIST}?${params}&clinic_id=${clinicId}`;
            const response = isPrint
                ? await RestAPIService.getAllPrint(url)
                : await RestAPIService.getAll(url);

            if (response?.data?.count) {
                setCardData(response?.data?.count)
            }

            if (isPrint) {
                const file = new Blob(
                    [response.data],
                    { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL);
            }

            if (response?.data?.status === 'success') {
                const dataWithSno = (response?.data?.data || []).map((item, index) => ({
                    ...item,
                    sno: index + 1,
                    request_type: 'Concession'
                }));
                setdataList(dataWithSno);
            } else {
                console.error('Failed:', response?.data?.message);
            }
        } catch (error) {
            if (error?.response?.data?.status === "fail") {
                errorMessage(error?.response?.data?.message);
            } else {
                errorMessage(error.message);
            }
        } finally {
            setIsLoader(false);
        }
    };

    const getMakerRequestData = () => {
        const fromDate = filterDatas.FromDate || '';
        const toDate = filterDatas.ToDate || '';
        const moduleType = filterDatas.module_type.join(',');  // Manually encoding
        const status = filterDatas.status.join(',');
        const patientType = filterDatas.Patient_type.join(',');

        // Manually constructing the query string
        const queryString = `from_date=${fromDate}&to_date=${toDate}&patient_name=${filterDatas.patient_Name || ""}&uhid=${filterDatas.UHID_No}&mobile=${filterDatas.phoneNumber}&module_type=${moduleType}&status=${status}&patient_type=${patientType}`;

        fetchStatusData(queryString);
    };

    const PrintData = () => {
        const fromDate = filterDatas.FromDate || '';
        const toDate = filterDatas.ToDate || '';
        const moduleType = (filterDatas.module_type.join(','));
        const status = (filterDatas.status.join(','));
        const patientType = (filterDatas.Patient_type.join(','));
        const exportType = 'pdf';

        // Manually constructing the query string
        const queryString = `from_date=${fromDate}&to_date=${toDate}&patient_name=${filterDatas.patient_Name || ""}&uhid=${filterDatas.UHID_No}&mobile=${filterDatas.phoneNumber}&module_type=${moduleType}&status=${status}&patient_type=${patientType}&export_type=${exportType}`;

        fetchStatusData(queryString, true);
    };

    useEffect(() => {
        getMakerRequestData();
    }, []);

    const handleFilterApply = () => {
        getMakerRequestData();
        setFilterOpen(false);
    };

    const handleFilterClear = () => {
        setFilterDatas({
            FromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            ToDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            DateIndex: 1,
            patient_Name: "",
            UHID_No: "",
            phoneNumber: null,
            module_type: ['IP', 'OP', 'RADIOLOGY', 'LAB', ...localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : []],      // All selected
            status: ['Pending', 'Billed', 'Accepted'],
            Patient_type: ['ip', 'op', 'self']
        });
        getMakerRequestData();
        setFilterOpen(false);
    };

    const gridToolBar = () => {
        let TextArray = [
            { label: "From Date", value: formatDate(filterDatas.FromDate) },
            { label: "To Date", value: formatDate(filterDatas.ToDate) },
        ]
        return (
            <Box>
                <CommonGridToolBarWithFilterText isDelete={0} FilterTextArray={TextArray} filename={""} />
            </Box>
        )
    }

    const Filter = () => {
        return (
            <Box component={"div"} className='eMed_Filter_Billing_Container'>
                <Box component={"div"} className='eMed_Filter_Top'>
                    <Typography variant='h6' fontWeight={"600"}>{t("Filter")}</Typography>
                    <Button onClick={() => { handleCloseFilter() }} className='eMed_usrconf_btn'>
                        <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
                    </Button>
                </Box>
                <Box component={"div"}>
                    <Box component={"div"} className='eMed_Filter_DateFilter'>
                        <Box style={{ width: "40%", }} component={"div"}>
                            <CommonDatePicker
                                FromDate={filterDatas.FromDate}
                                ToDate={filterDatas.ToDate}
                                DateIndex={filterDatas.DateIndex}
                                SelectedDates={handleDateChange}
                                HideAllMenu={true}
                            />
                        </Box>
                    </Box>
                    <Box component={"div"} display={"flex"} flexDirection={"row"} marginLeft={'1vw'}>
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            label={t('Patient Name')}
                            value={filterDatas.patient_Name}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.ALPHA_NUM_SPLCHAR(e.target.value);
                                if (number || e.target.value === "") {
                                    let newValue = e.target.value.toUpperCase();
                                    if (filterDatas.patient_Name !== newValue) {
                                        setFilterDatas((prevstate) => ({
                                            ...prevstate,
                                            patient_Name: newValue,
                                        }));
                                    }
                                }
                            }}
                        />
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            sx={{ marginLeft: '1vw' }}
                            label={`UHID`}
                            value={filterDatas.UHID_No}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.ALPHA_NUM_SPLCHAR(e.target.value);
                                if (number || e.target.value === "") {
                                    setFilterDatas((prevstate) => ({
                                        ...prevstate,
                                        UHID_No: e.target.value
                                    }))
                                }
                            }
                            }
                        />
                    </Box>
                    <Box component={"div"} display={"flex"} flexDirection={"row"} marginLeft={'1vw'}>
                        <TextField
                            className="eMed_Bill_Filter_TextFeild_small"
                            size='small'
                            label={`Mobile No`}
                            value={filterDatas.phoneNumber}
                            autoComplete='off'
                            onChange={(e) => {
                                let number = CommonValidation.numberOnly(e.target.value);
                                if ((number && e.target.value.length < 11) || e.target.value === "") {
                                    setFilterDatas((prevstate) => ({
                                        ...prevstate,
                                        phoneNumber: e.target.value
                                    }))
                                }
                            }
                            }
                        />
                    </Box>
                    {/* Module Type */}
                    <Box component={"div"}>
                        <Box className="eMed_Filter_Header">
                            <Typography marginLeft={"1vw"}>Module Type</Typography>
                        </Box>
                        <Box className="eMed_CheckBox_Group">
                            {['IP', 'OP', 'RADIOLOGY', 'LAB', ...localGetItem("Investigation_List") ? JSON.parse(localGetItem("Investigation_List")) : []].map((type) => (
                                <FormControlLabel
                                    key={type}
                                    control={
                                        <Checkbox
                                            checked={filterDatas.module_type.includes(type)}
                                            onChange={() => handleCheckboxChange('module_type', type)}
                                        />
                                    }
                                    label={type.toUpperCase()}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Status */}
                    <Box component={"div"}>
                        <Box className="eMed_Filter_Header">
                            <Typography marginLeft={"1vw"}>Status</Typography>
                        </Box>
                        <Box className="eMed_CheckBox_Group">
                            {['Pending', 'Accepted', 'Rejected', 'Cancelled'].map((status) => (
                                <FormControlLabel
                                    key={status}
                                    control={
                                        <Checkbox
                                            checked={filterDatas.status.includes(status)}
                                            onChange={() => handleCheckboxChange('status', status)}
                                        />
                                    }
                                    label={status.charAt(0).toUpperCase() + status.slice(1)}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Patient Type
                    <Box component={"div"}>
                        <Box className="eMed_Filter_Header">
                            <Typography marginLeft={"1vw"}>Patient Type</Typography>
                        </Box>
                        <Box className="eMed_CheckBox_Group">
                            {['ip', 'op', 'self'].map((type) => (
                                <FormControlLabel
                                    key={type}
                                    control={
                                        <Checkbox
                                            checked={filterDatas.Patient_type.includes(type)}
                                            onChange={() => handleCheckboxChange('Patient_type', type)}
                                        />
                                    }
                                    label={type.toUpperCase()}
                                />
                            ))}
                        </Box>
                    </Box> */}

                    {/* clear and search button */}
                    <Box component={"div"} className="eMed_Filter_Btn_Div">
                        <Stack direction="row" spacing={2} mt={2}>
                            <Button variant="outlined" onClick={handleFilterClear}>
                                {t("Clear")}
                            </Button>
                            <Button variant="contained" onClick={handleFilterApply}>
                                {t("Search")}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        )
    };

    const columns = [
        {
            field: "sno", headerName: t("S.No"), flex: 0.05, headerAlign: "center", align: "center",
            renderCell: (params) => (<Box component={'div'}>{params?.row?.sno ? params?.row?.sno : "-"}</Box>)
        },
        {
            field: "patient_name",
            flex: 1.5,
            headerName: t("PatientDetails"),
            renderCell: (params) => (
                <Box>
                    <CommonPatientDetails
                        disablePhoto={true}
                        data={params?.row}
                        fromCreditBill={true}
                    />
                </Box>
            ),
            valueGetter: (params) => {
                const name = params.row?.patient_name || "";
                const mobile = params.row?.mobile || "";
                const UHID = params.row?.uhid || "";
                return `${name} ${mobile} ${UHID}`;
            }
        },
        {
            field: "request_number",
            flex: 1.8,
            headerName: t("Request Details"),
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Box>
                    <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{params?.row?.request_number ? params?.row?.request_number : "-"}</div>
                    <div style={{ fontSize: "0.8rem" }}>{params?.row?.request_date ? formatDate(params?.row?.request_date) + ' |' + params?.row?.request_time : "-"}</div>
                </Box>
            )
        },
        {
            field: "request_type", headerName: t("Request Type"), flex: 1, renderCell: (params) => (
                <Box>
                    {params.row?.request_type ? params.row?.request_type : 'Concession'}
                </Box>
            )
        },
        {
            field: "requested_by", headerName: t("Requested By"), flex: 1.5, renderCell: (params) => (
                <Box>
                    {params?.row?.requested_by?.length > 18 ?
                        <Tooltip placement='top' title={params?.row?.requested_by} arrow>
                            <div>{params?.row?.requested_by.slice(0, 18) + "..."}</div></Tooltip> :
                        params?.row?.requested_by ? params?.row?.requested_by : "-"}
                </Box>
            )
        },
        {
            field: "request_status", headerName: t("Status"), flex: 1, renderCell: (params) => (
                <Box>{params.row?.request_status === "Requested" ? "Pending" : params.row?.request_status}</Box>
            ),
            valueGetter: (params) => {
                const status = params.row?.request_status === "Requested" ? "Pending" : params.row?.request_status;
                return `${status}`;
            }
        },
        {
            field: "module_type", headerName: t("Module"), flex: 1, renderCell: (params) => (
                <Box>{params.row?.module_type ? params.row?.module_type : '-'}</Box>
            )
        },
        {
            field: "approver_name", headerName: t("Action Details"), flex: 1.8, renderCell: (params) => (
                <Box>
                    <div style={{ fontSize: "0.9rem", fontWeight: "600" }} > {params?.row?.action_by ? params?.row?.action_by : "-"}</div >
                    <div style={{ fontSize: "0.8rem" }}>{params?.row?.action_date ? params?.row?.action_date : "-"}</div>
                </Box >
            )
        },
        {
            field: "remarks", headerName: t("Remarks"), flex: 2.5, renderCell: (params) => (
                <Box>
                    {params?.row?.remarks?.length > 20 ?
                        <Tooltip placement='top' title={params?.row?.remarks} arrow>
                            <div>{params?.row?.remarks.slice(0, 18) + "..."}</div></Tooltip> :
                        params?.row?.remarks ? params?.row?.remarks : "-"}
                </Box>
            )
        },
    ];

    return (
        <Box component="div" className="eMed_Main_container">

            {/* Stats and Filter Container */}
            <Box component="div" className="eMed_Top_Container">
                <Box component="div" className="eMed_Cards_Container">
                    {AmountsCard(t("Total Transactions"), cardData?.total || 0, true)}
                    {AmountsCard(t("Pending"), cardData?.pending || 0, true)}
                    {AmountsCard(t("Accepted"), cardData?.accepted || 0, true)}
                    {AmountsCard(t("Rejected"), cardData?.rejected || 0, true)}
                    {AmountsCard(t("Cancelled"), cardData?.cancelled || 0, true)}
                </Box>

                {/* Filter and Print Actions */}
                <Box component="div" className="eMed_Action_Container">
                    <Box component="div" className="eMed_Filter_Icon_div">
                        <Tooltip title="Filter" placement="top" arrow>
                            <Button className="eMed_usrconf_btn" onClick={handleOpenFilter}>
                                <img src={ImagePaths.Filter.default} alt="filter" className="eMed_action_img" />
                            </Button>
                        </Tooltip>
                    </Box>
                    <Tooltip title="Print" placement="top" arrow>
                        <Button className="eMed_usrconf_btn" onClick={PrintData}>
                            <img src={ImagePaths.LabPrintIcon.default} alt="print" className="eMed_action_img" />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            {/* Table Container */}
            <Box component="div" className="eMed_Table_Container">
                <Stack component="div">
                    <Paper className="eMed_Table_Wrapper">
                        <DataGrid
                            rows={dataList}
                            columns={columns}
                            page={page}
                            pageSize={pageSize}
                            hideFooterSelectedRowCount
                            localeText={{
                                toolbarColumns: "",
                                toolbarDensity: "",
                                toolbarExport: "",
                                toolbarFilters: "",
                                toolbarExportPrint: ""
                            }}
                            headerHeight={40}
                            components={{
                                Toolbar: gridToolBar,
                                NoRowsOverlay: () => (
                                    <Stack className="eMed_conf_nodata">
                                        {t("NoRecordsFound")}
                                    </Stack>
                                )
                            }}
                            rowsPerPageOptions={[10, 20, 30]}
                            disableSelectionOnClick
                            pagination
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            loading={isLoader}
                        />
                    </Paper>
                </Stack>
            </Box>

            {/* Drawer for Filter */}
            <Drawer
                anchor="right"
                open={filterOpen}
                onClose={handleCloseFilter}
            >
                {Filter()}
            </Drawer>

            {isErrorMsg && (
                <ToastMsg
                    severity="error"
                    msg={isErrorMsgText}
                    msgPop={() => setIsErrorMsg(false)}
                />
            )}
        </Box>
    );
};

export default MakerApprovalTransaction;
